import { format } from "date-fns/fp";
import React, { useCallback, useEffect, useState } from "react";
import {
  Pressable,
  SectionList,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { useDispatch } from "react-redux";
import BackArrow from "@assets/svg/BackArrow";
import Text from "@components/CustomText";
import { LOGOUT_INFO_EVENTS } from "@constants/analyticsEventsCT";
import color from "@constants/color";
import t from "@constants/localization";
import { colors } from "@constants/theme";
import { actions as snackbarActions } from "@reducers/snackbarReducer";
import analytics from "@services/analytics";
import { getLoggedInDevices, logoutLeader } from "@services/logout";
import LogoutConfirmationModal from "./LogoutConfirmationModal";
import Loading from "./Views/Loading";

function formattedDate(dateString) {
  const date = new Date(dateString);

  if (isNaN(date)) {
    return "";
  }
  const formatted = format("dd-MM-yy, hh:mm a")(date);
  return formatted;
}

const _keyExtractor = (item, index) => index;
const PATH = "screens/LoggedInDevices";

const LoggedInDevices = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const [apiData, setApiData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const {
    logged_in_devices: loggedInDevices = [],
    current_device: currentDevice = null,
  } = apiData || {};

  const fetchLoggedInData = () => {
    getLoggedInDevices()
      .then((response) => {
        if (response.data) setApiData(response.data);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  useEffect(() => {
    fetchLoggedInData();
  }, []);

  const [showModal, setShowModal] = useState(route?.params?.fromModal || false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);

  useEffect(() => {
    analytics.trackEvent(LOGOUT_INFO_EVENTS.LOGOUT_PAGE_LANDED, {});
  }, []);

  const goBackHandler = () => {
    navigation.goBack();
  };

  const showSnackbar = (msg = "") => {
    const snackbarPayload = {
      message: msg,
      autoHide: true,
      actionText: "Okay",
      actionHandler: (_e) => {
        dispatch(snackbarActions.hideSnackbar());
      },
    };
    dispatch(snackbarActions.showSnackbar(snackbarPayload));
  };

  const logOut = (deviceId) => {
    const id = deviceId || selectedDeviceId;
    setSelectedDeviceId(id);
    logoutLeader(id, "LOGGED_IN_DEVICES")
      .then((res) => {
        if (res?.data?.toastMessage) {
          showSnackbar(res.data.toastMessage);
        }
        analytics.trackEvent(LOGOUT_INFO_EVENTS.SUBMIT_LOGOUT_BUTTON, {});
      })
      .catch((e) => console.log(e))
      .finally(() => {
        fetchLoggedInData();
        if (showModal) {
          onCancel();
        }
      });
  };
  const onCancel = () => {
    navigation.setParams({
      fromModal: false,
      deviceId: null,
    });
    setShowModal(false);
  };

  const openModal = (deviceId) => {
    if (currentDevice.auto_read_otp) {
      setShowModal(true);
    } else {
      logOut(deviceId);
    }
  };

  const renderItems = ({ item }) => {
    if (!item) return null;
    const {
      device_info: { brand = t(`${PATH}/unknownDevice`) } = {},
      updated_at: updatedAt,
      unique_device_id: deviceId,
    } = item || {};

    const date = formattedDate(updatedAt);

    return (
      <View style={styles.itemContainer}>
        <View style={styles.textContainer}>
          <Text style={styles.deviceName} newfont={true} variant="body_text_2">
            {brand}
          </Text>
          <Text style={styles.deviceOS} newfont={true} variant="caption_2">
            {t(`${PATH}/android`)}
            {date ? `, ${date}` : ""}
          </Text>
        </View>
        <Pressable
          // TODO: Define better testID conventions
          testID="logout"
          onPress={() => {
            openModal(deviceId);
          }}
        >
          <Text newfont={true} variant="body_text_2" style={styles.logoutText}>
            {t(`${PATH}/logout`)}
          </Text>
        </Pressable>
      </View>
    );
  };

  const renderSectionHeaders = useCallback(({ section: { title, data } }) => {
    if (Array.isArray(data) && data.length) {
      return (
        <Text newfont={true} variant="body_text_4" style={styles.sectionText}>
          {title}
        </Text>
      );
    }
    return null;
  }, []);

  const _sections = [
    {
      title: t(`${PATH}/yourDevices`),
      data: currentDevice ? [{ ...currentDevice }] : [],
    },
    { title: t(`${PATH}/otherDevices`), data: loggedInDevices },
  ];

  return (
    <View style={styles.mainContainer}>
      <View style={styles.topNavContainer}>
        <TouchableOpacity
          onPress={goBackHandler}
          style={styles.arrowCont}
          testID="go_back"
        >
          <BackArrow />
        </TouchableOpacity>
        <Text newfont={true} variant="title_20">
          {t(`${PATH}/allLoginDevices`)}
        </Text>
      </View>
      <Loading isLoading={isLoading} />
      <SectionList
        sections={_sections}
        keyExtractor={_keyExtractor}
        renderItem={renderItems}
        renderSectionHeader={renderSectionHeaders}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        style={styles.listContainer}
        contentContainerStyle={styles.contentContainer}
      />
      <LogoutConfirmationModal
        closeModal={onCancel}
        showModal={showModal}
        onLogout={logOut}
        onCancel={onCancel}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    backgroundColor: color.white,
  },
  listContainer: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
    paddingBottom: 16,
  },
  topNavContainer: {
    flexDirection: "row",
    height: 56,
    backgroundColor: colors.white,
    alignItems: "center",
    alignContent: "center",
    paddingHorizontal: 16,
  },
  arrowCont: {
    marginEnd: 20,
  },
  sectionText: {
    marginHorizontal: 16,
    color: color.lightBlack,
    marginBottom: 8,
  },
  itemContainer: {
    elevation: 4,
    backgroundColor: "white",
    padding: 16,
    borderRadius: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginHorizontal: 16,
    marginBottom: 16,
  },
  deviceName: {
    color: color.mediumBlack,
  },
  textContainer: {
    flex: 0.9,
  },
  deviceOS: {
    color: color.lightBlack,
    marginTop: 8,
  },
  logoutText: {
    fontSize: 16,
    color: color.primary,
  },
});

export default LoggedInDevices;
